import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  // Public routes
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { requiresAuth: false },
  },

  // Protected routes
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true },
  },

  // Redirects for old routes
  {
    path: '/candidates',
    redirect: '/campaigns',
  },
  {
    path: '/candidates/:id',
    redirect: '/campaigns',
  },
  {
    path: '/jobs',
    redirect: '/campaigns',
  },
  {
    path: '/jobs/:id',
    redirect: (to) => `/campaigns/${to.params['id']}`,
  },

  // Campaign routes
  {
    path: '/campaigns',
    name: 'campaigns',
    component: () => import('@/views/campaigns/CampaignListView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/campaigns/:id',
    name: 'campaign-detail',
    component: () => import('@/views/campaigns/CampaignDetailView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/campaigns/:id/validation',
    name: 'campaign-validation',
    component: () => import('@/views/validation/CVValidationView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/evaluations',
    name: 'evaluations',
    component: () => import('@/views/evaluations/EvaluationListView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/evaluations/new',
    name: 'evaluation-new',
    component: () => import('@/views/evaluations/NewEvaluationView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/evaluations/:id',
    name: 'evaluation-detail',
    component: () => import('@/views/evaluations/EvaluationDetailView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/settings',
    name: 'admin-settings',
    component: () => import('@/views/admin/AdminSettingsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/superadmin',
    name: 'superadmin-dashboard',
    component: () => import('@/views/superadmin/SuperAdminDashboard.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true },
  },
  {
    path: '/superadmin/tenants',
    name: 'superadmin-tenants',
    component: () => import('@/views/superadmin/TenantsListView.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true },
  },
  {
    path: '/superadmin/tenants/new',
    name: 'superadmin-tenant-new',
    component: () => import('@/views/superadmin/TenantFormView.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true },
  },
  {
    path: '/superadmin/tenants/:id',
    name: 'superadmin-tenant-detail',
    component: () => import('@/views/superadmin/TenantDetailView.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true },
  },
  {
    path: '/superadmin/tenants/:id/edit',
    name: 'superadmin-tenant-edit',
    component: () => import('@/views/superadmin/TenantFormView.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true },
  },
  {
    path: '/superadmin/users',
    name: 'superadmin-users',
    component: () => import('@/views/superadmin/UsersManagementView.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true },
  },

  // Catch-all 404
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/HomeView.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// Navigation guard for authentication
router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore()

  // Check if route requires super admin
  const requiresSuperAdmin = to.matched.some((record) => record.meta['requiresSuperAdmin'])

  if (requiresSuperAdmin && authStore.user?.role !== 'super_admin') {
    // Redirect non-super-admin to regular dashboard
    next({ name: 'dashboard' })
    return
  }

  // Check if route requires authentication
  const requiresAuth = to.matched.some((record) => record.meta['requiresAuth'])

  if (requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login if not authenticated
    next({
      name: 'login',
      query: { redirect: to.fullPath }, // Save intended destination
    })
  } else if (
    (to.name === 'login' || to.name === 'register') &&
    authStore.isAuthenticated
  ) {
    // Redirect to dashboard if already authenticated
    next({ name: 'dashboard' })
  } else {
    next()
  }
})

export default router
